<template>
  <v-app>
    <v-main class="main">

      <!-- Sidebar -->
      <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event" />

      <v-container fluid class="main-content pa-4">

        <!-- Header -->
        <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar" />

        <!-- Stats -->
        <v-row class="stats mt-4" dense>
          <v-col cols="12" sm="3">
            <StatCard title="Total Requests" :value="totalTransationsCount" subtext="Across all investment"
              icon="mdi-counter" iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Approved Requests" :value="approvedTransations" icon="mdi-timer-check-outline"
              iconColor="#ef4444" :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Pending Requests" :value="pendingTransations" icon="mdi-timer-pause-outline"
              iconColor="#22c55e" :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Processing Requests" :value="processingTransations" icon="mdi-timer-sync-outline"
              iconColor="#ef4444" :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
        </v-row>

        <v-row class="stats mt-4" dense>
          <v-col cols="12" sm="3">
            <StatCard title="Rejected Requests" :value="rejectedTransations"
              :subtext="`From ${totalTransationsCount} Total Transactions`" icon="mdi-timer-remove-outline"
              iconColor="#22c55e" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Failed Requests" :value="failedTransations" icon="mdi-timer-stop-outline"
              iconColor="#ef4444" :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Paid Requests" :value="paidTransations" icon="mdi-timer-pause-outline" iconColor="#22c55e"
              :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
          <v-col cols="12" sm="3">
            <StatCard title="Total Successful Requests" :value="totalSuccesfullTransations"
              icon="mdi-timer-sync-outline" iconColor="#ef4444"
              :subtext="`From ${totalTransationsCount} Total Transactions`" />
          </v-col>
        </v-row>

        <!-- Table Card -->
        <v-card class="table-card mt-6">

          <div class="table-header flex justify-between align-center flex-wrap">
            <!-- Left side: search + clear -->
            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
              <InputText v-model="filters['global'].value" placeholder="Search Transaction..." />
              <Button type="button" icon="pi pi-filter-slash" label="Clear" variant="outlined" @click="clearFilters" />
            </div>
          </div>

          <!-- PrimeVue DataTable with search -->
          <DataTable v-model:filters="filters" :value="transactions" paginator :rows="10" dataKey="id" stripedRows
            class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]" tableStyle="min-width: 50rem"
            filterDisplay="menu" :globalFilterFields="['transaction_id', 'status', 'transaction_source', 'type']">

            <!-- Empty table -->
            <template #empty>
              No Transaction found.
            </template>

            <!-- Loading state -->
            <template #loading>
              Loading Transaction...
            </template>

            <!-- Product Column -->
            <Column header="Transaction ID">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.transaction_id }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--Amount Column-->
            <Column header="Amount">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ formatCurrency(slotProps.data.amount) }}
                  </div>
                </div>
              </template>
            </Column>

            <!--User Column-->
            <Column header="Bank Name">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    <div class="product-name">{{ slotProps.data.bank_name }}</div>
                  </div>
                </div>
              </template>
            </Column>

            <!--Type Column-->
            <Column header="Account Name">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.account_name }}
                  </div>
                </div>
              </template>
            </Column>

            <!--Type Column-->
            <Column header="Account Number">
              <template #body="slotProps">
                <div class="product-cell">
                  <div>
                    {{ slotProps.data.account_number }}
                  </div>
                </div>
              </template>
            </Column>

            <!-- Status Column -->
            <Column header="Status">
              <template #body="slotProps">
                <span :class="[
                  'status-pill',
                  getStatusClass(slotProps.data.status)
                ]">
                  {{ formatStatus(slotProps.data.status) }}
                </span>
              </template>
            </Column>

            <!-- Created Column -->
            <Column header="Request Date and Time">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.created_at) }}
              </template>
            </Column>

            <!-- Created Column -->
            <Column header="Updated At">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.updated_at) }}
              </template>
            </Column>

            <!-- Actions Column -->
            <Column header="" style="width:120px">
              <template #body="slotProps">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon variant="text" v-bind="props">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>

                  <v-list density="compact">
                    <v-list-item @click="updateStatus(slotProps.data, 'approved')">
                      <template #prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title class="text-success">
                        Approved
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="updateStatus(slotProps.data, 'paid')">
                      <template #prepend>
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </template>
                      <v-list-item-title class="text-success">
                        Paid
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="updateStatus(slotProps.data, 'processing')">
                      <template #prepend>
                        <v-icon color="warning">mdi-progress-clock</v-icon>
                      </template>
                      <v-list-item-title class="text-warning">
                        Processing
                      </v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="updateStatus(slotProps.data, 'rejected')">
                      <template #prepend>
                        <v-icon color="error">mdi-close-circle</v-icon>
                      </template>
                      <v-list-item-title class="text-error">
                        Rejected
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="updateStatus(slotProps.data, 'failed')">
                      <template #prepend>
                        <v-icon color="error">mdi-alert-circle</v-icon>
                      </template>
                      <v-list-item-title class="text-error">
                        Failed
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </Column>

          </DataTable>
        </v-card>
        <!-- Edit Modal -->
        <!-- <v-dialog v-model="editDialog" max-width="500px">
          <v-card>
            <v-card-title>Edit Product</v-card-title>
            <v-card-text>
              <v-text-field v-model="selectedProduct.product_name" label="Product Name" />
              <v-textarea v-model="selectedProduct.description" label="Description" />
              <v-switch v-model="selectedProduct.is_active" label="Active" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="closeEditModal">Cancel</v-btn>
              <v-btn color="primary" @click="saveEdit">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog> -->
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import StatCard from "@/components/StatCard.vue"
import { useTransactStore } from "@/store/transaction"
import { formatCurrency, formatDate } from "@/util"
import {executeUpdateRequestStatus} from "@/api/transaction"


const tr = useTransactStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const filters = ref({ global: { value: null, matchMode: "contains" } })

/* Mobile */
const toggleSidebar = () => {
  if (isMobile.value) drawerOpen.value = !drawerOpen.value
  else isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)
  
  try {
    await tr.loadWithdrawalRequests()
  } catch (error) {
    console.error(error)
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Transaction */
const transactions = computed(() => tr.withdraws || [])

/* Stats */
//  const totalTransationsAmount = computed(() =>
//   transactions.value.reduce((sum, tr) => sum + Number(tr.amount || 0), 0)
// )

//total succesfull transaction
const totalSuccesfullTransations = computed(() =>
  transactions.value
    ?.filter(tr => tr.status === "successful")
    .reduce((sum, tr) => sum + Number(tr.amount || 0), 0)
)

const approvedTransations = computed(() =>
  transactions.value.filter(i => i.status === "approved").length
)

const pendingTransations = computed(() =>
  transactions.value.filter(i => i.status === "pending").length
)

const processingTransations = computed(() =>
  transactions.value.filter(i => i.status === "processing").length
)

const paidTransations = computed(() =>
  transactions.value.filter(i => i.status === "paid").length
)

const rejectedTransations = computed(() =>
  transactions.value.filter(i => i.status === "rejected").length
)

const failedTransations = computed(() =>
  transactions.value.filter(i => i.status === "failed").length
)

const totalTransationsCount = computed(() => transactions.value.length)

/* Clear filters */
const clearFilters = () => {
  filters.value = { global: { value: null, matchMode: "contains" } }
}

const getStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'paid':
      return 'status-success'

    case 'processing':
      return 'status-processing'

    case 'failed':
    case 'rejected':
      return 'status-error'

    default:
      return 'status-default'
  }
}

const formatStatus = (status) => {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const snackbar = ref({
  show: false,
  message: "",
  color: "success" // success | error | warning | info
})

const showSnackbar = (message, color = "success") => {
  snackbar.value.message = message
  snackbar.value.color = color
  snackbar.value.show = true
}

//update status of a wallet request
const updateStatus = async (row, status) => {
  console.log("Row:", row)
  console.log("ID:", row.uuid)
  console.log(`Updating status for request ID ${row.uuid} to ${status}`)
  try {
    const payload = {
      status
    }

    await executeUpdateRequestStatus(row.uuid, payload)

    // Refresh your table or update the row locally
    await tr.loadWithdrawalRequests()

     showSnackbar(`Status updated to ${status}.`)
  } catch (error) {
    console.error(error)
  }
}

</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Product */
.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-image {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.product-name {
  font-weight: 700;
}

.product-desc {
  font-size: 16px;
  color: #64748b;
}

/* Status */
.status-pill {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.status-success {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-processing {
  background-color: #fff3e0;
  color: #ef6c00;
}

.status-error {
  background-color: #ffebee;
  color: #c62828;
}

.status-default {
  background-color: #f5f5f5;
  color: #f33232;
}

/* Search input */
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #64748b;
}

.search-wrapper input {
  padding-left: 28px;
}

/* Button */
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}
</style>
